import { useState, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { ShieldCheck, Smartphone, Save, Star, Pencil, X, Check, ToggleLeft, ToggleRight, Eye, EyeOff } from "lucide-react"

import * as api from "@/api/cogeser"
import { useAuth } from "@/hooks/useAuth"
import Admins from "../Admins"

// ----------------------------------------------------------------------------

type Tab = "admins" | "rules" | "mobile"

export default function Settings() {
  const [activeTab, setActiveTab] = useState<Tab>("admins")

  const tabs: { key: Tab; label: string; icon: typeof ShieldCheck }[] = [
    { key: "admins", label: "Amministratori", icon: ShieldCheck },
    { key: "rules", label: "Regole Punti", icon: Star },
    { key: "mobile", label: "App Mobile", icon: Smartphone },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Impostazioni</h1>
        <p className="mt-1 text-sm text-gray-500">Configurazione della piattaforma</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      {activeTab === "admins" && <Admins />}
      {activeTab === "rules" && <LoyaltyRulesTab />}
      {activeTab === "mobile" && <MobileSettingsTab />}
    </div>
  )
}

// ============================================================================
// Loyalty Rules Tab
// ============================================================================

interface LoyaltyRule {
  code: string
  label: string
  points: number
  category: "earn" | "redeem" | "adjustment"
  frequency: "one_time" | "recurring" | "repeatable"
  active: boolean
  visible: boolean
  metadata: Record<string, any>
}

const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
  earn: { label: "Accumulo", color: "bg-green-100 text-green-700" },
  redeem: { label: "Riscatto", color: "bg-blue-100 text-blue-700" },
  adjustment: { label: "Rettifica", color: "bg-gray-100 text-gray-600" },
}

function LoyaltyRulesTab() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const [editingCode, setEditingCode] = useState<string | null>(null)
  const [editLabel, setEditLabel] = useState("")
  const [editPoints, setEditPoints] = useState("")
  const [editMetaTitle, setEditMetaTitle] = useState("")
  const [editMetaDescription, setEditMetaDescription] = useState("")

  const { data: rulesData, isLoading } = useQuery({
    queryKey: ["loyalty-rules"],
    queryFn: () => api.getLoyaltyRules(token!) as Promise<LoyaltyRule[]>,
    enabled: !!token,
  })

  const rules = (rulesData || []) as LoyaltyRule[]

  const updateMutation = useMutation({
    mutationFn: ({ code, body }: { code: string; body: Record<string, unknown> }) =>
      api.updateLoyaltyRule(code, body, token!),
    onSuccess: () => {
      toast.success("Regola aggiornata")
      queryClient.invalidateQueries({ queryKey: ["loyalty-rules"] })
      setEditingCode(null)
    },
    onError: () => toast.error("Errore nell'aggiornamento"),
  })

  const toggleMutation = useMutation({
    mutationFn: (body: { code: string; active?: boolean; visible?: boolean }) => {
      const { code, ...updates } = body
      return api.updateLoyaltyRule(code, updates, token!)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loyalty-rules"] })
    },
    onError: () => toast.error("Errore nell'aggiornamento"),
  })

  function startEdit(rule: LoyaltyRule) {
    setEditingCode(rule.code)
    setEditLabel(rule.label)
    setEditPoints(String(rule.points))
    setEditMetaTitle(rule.metadata?.metaTitle || "")
    setEditMetaDescription(rule.metadata?.metaDescription || "")
  }

  function saveEdit() {
    if (!editingCode) return
    const currentRule = rules.find((r) => r.code === editingCode)
    const updatedMetadata = {
      ...(currentRule?.metadata || {}),
      metaTitle: editMetaTitle,
      metaDescription: editMetaDescription,
    }
    updateMutation.mutate({
      code: editingCode,
      body: { label: editLabel, points: parseInt(editPoints) || 0, metadata: updatedMetadata },
    })
  }

  function cancelEdit() {
    setEditingCode(null)
  }

  if (isLoading) {
    return <div className="py-8 text-center text-sm text-gray-500">Caricamento...</div>
  }

  const earnRules = rules.filter((r) => r.category === "earn")
  const otherRules = rules.filter((r) => r.category !== "earn")

  return (
    <div className="space-y-6">
      {/* Earn rules */}
      <RulesTable
        title="Regole accumulo punti"
        subtitle="Configurazione dei punti attribuiti per ciascuna attività"
        rules={earnRules}
        editingCode={editingCode}
        editLabel={editLabel}
        editPoints={editPoints}
        editMetaTitle={editMetaTitle}
        editMetaDescription={editMetaDescription}
        setEditLabel={setEditLabel}
        setEditPoints={setEditPoints}
        setEditMetaTitle={setEditMetaTitle}
        setEditMetaDescription={setEditMetaDescription}
        onStartEdit={startEdit}
        onSave={saveEdit}
        onCancel={cancelEdit}
        onToggleActive={(code, active) => toggleMutation.mutate({ code, active })}
        onToggleVisible={(code, visible) => toggleMutation.mutate({ code, visible })}
        isPending={updateMutation.isPending}
      />

      {/* Redeem / adjustment rules */}
      {otherRules.length > 0 && (
        <RulesTable
          title="Regole riscatto e rettifica"
          subtitle="Regole per la sottrazione punti e le rettifiche manuali"
          rules={otherRules}
          editingCode={editingCode}
          editLabel={editLabel}
          editPoints={editPoints}
          editMetaTitle={editMetaTitle}
          editMetaDescription={editMetaDescription}
          setEditLabel={setEditLabel}
          setEditPoints={setEditPoints}
          setEditMetaTitle={setEditMetaTitle}
          setEditMetaDescription={setEditMetaDescription}
          onStartEdit={startEdit}
          onSave={saveEdit}
          onCancel={cancelEdit}
          onToggleActive={(code, active) => toggleMutation.mutate({ code, active })}
          onToggleVisible={(code, visible) => toggleMutation.mutate({ code, visible })}
          isPending={updateMutation.isPending}
        />
      )}
    </div>
  )
}

// ----------------------------------------------------------------------------

function RulesTable({
  title,
  subtitle,
  rules,
  editingCode,
  editLabel,
  editPoints,
  editMetaTitle,
  editMetaDescription,
  setEditLabel,
  setEditPoints,
  setEditMetaTitle,
  setEditMetaDescription,
  onStartEdit,
  onSave,
  onCancel,
  onToggleActive,
  onToggleVisible,
  isPending,
}: {
  title: string
  subtitle: string
  rules: LoyaltyRule[]
  editingCode: string | null
  editLabel: string
  editPoints: string
  editMetaTitle: string
  editMetaDescription: string
  setEditLabel: (v: string) => void
  setEditPoints: (v: string) => void
  setEditMetaTitle: (v: string) => void
  setEditMetaDescription: (v: string) => void
  onStartEdit: (rule: LoyaltyRule) => void
  onSave: () => void
  onCancel: () => void
  onToggleActive: (code: string, active: boolean) => void
  onToggleVisible: (code: string, visible: boolean) => void
  isPending: boolean
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-6 py-4">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Codice</th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Descrizione</th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Tipo</th>
            <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Punti</th>
            <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Attiva</th>
            <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">App</th>
            <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Azioni</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rules.map((rule) => {
            const isEditing = editingCode === rule.code
            const cat = CATEGORY_LABELS[rule.category] || CATEGORY_LABELS.earn
            const ruleType = rule.frequency as string | undefined

            return (
              <tr key={rule.code} className={`hover:bg-gray-50 ${!rule.active ? "opacity-50" : ""}`}>
                <td className="px-6 py-3">
                  <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">{rule.code}</span>
                </td>
                <td className="px-6 py-3">
                  {isEditing ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        placeholder="Label backoffice"
                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                      />
                      <input
                        type="text"
                        value={editMetaTitle}
                        onChange={(e) => setEditMetaTitle(e.target.value)}
                        placeholder="Titolo app mobile"
                        className="w-full rounded-lg border border-blue-200 bg-blue-50/50 px-2 py-1 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                      />
                      <textarea
                        value={editMetaDescription}
                        onChange={(e) => setEditMetaDescription(e.target.value)}
                        placeholder="Descrizione app mobile"
                        rows={2}
                        className="w-full rounded-lg border border-blue-200 bg-blue-50/50 px-2 py-1 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                      />
                    </div>
                  ) : (
                    <div>
                      <span className="text-sm text-gray-900">{rule.label}</span>
                      {rule.metadata?.metaTitle && (
                        <p className="mt-0.5 text-xs text-blue-500">{rule.metadata.metaTitle}</p>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-6 py-3">
                  <div className="flex flex-col gap-1">
                    <span className={`inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-medium ${cat.color}`}>{cat.label}</span>
                    {ruleType && (
                      <span className="text-xs text-gray-400">{ruleType === "one_time" ? "Una tantum" : ruleType === "recurring" ? "Ricorrente" : ruleType === "repeatable" ? "Ripetitiva" : ruleType}</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-3 text-right">
                  {isEditing ? (
                    <input
                      type="number"
                      value={editPoints}
                      onChange={(e) => setEditPoints(e.target.value)}
                      className="w-20 rounded-lg border border-gray-300 px-2 py-1 text-right text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  ) : (
                    <span className={`text-sm font-semibold ${rule.points > 0 ? "text-green-600" : rule.points < 0 ? "text-red-600" : "text-gray-500"}`}>
                      {rule.points > 0 ? `+${rule.points}` : rule.points === 0 ? "variabile" : rule.points}
                    </span>
                  )}
                </td>
                <td className="px-6 py-3 text-center">
                  <button
                    onClick={() => onToggleActive(rule.code, !rule.active)}
                    className="text-gray-500 transition-colors hover:text-gray-700"
                    title={rule.active ? "Disattiva regola" : "Attiva regola"}
                  >
                    {rule.active ? (
                      <ToggleRight className="h-6 w-6 text-primary" />
                    ) : (
                      <ToggleLeft className="h-6 w-6" />
                    )}
                  </button>
                </td>
                <td className="px-6 py-3 text-center">
                  <button
                    onClick={() => onToggleVisible(rule.code, !rule.visible)}
                    className="transition-colors hover:text-gray-700"
                    title={rule.visible ? "Nascondi da app" : "Mostra in app"}
                  >
                    {rule.visible ? (
                      <Eye className="h-5 w-5 text-blue-500" />
                    ) : (
                      <EyeOff className="h-5 w-5 text-gray-300" />
                    )}
                  </button>
                </td>
                <td className="px-6 py-3 text-right">
                  {isEditing ? (
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={onSave}
                        disabled={isPending}
                        className="rounded-lg p-1.5 text-green-600 hover:bg-green-50"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button onClick={onCancel} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => onStartEdit(rule)}
                      className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ============================================================================
// Mobile Settings Tab
// ============================================================================

function MobileSettingsTab() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const [minVersion, setMinVersion] = useState("")
  const [loaded, setLoaded] = useState(false)

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ["mobile-settings"],
    queryFn: () => api.getMobileSettings(token!) as Promise<Record<string, any>>,
    enabled: !!token,
  })

  useEffect(() => {
    if (settingsData && !loaded) {
      setMinVersion((settingsData as Record<string, any>).minAppVersion || "")
      setLoaded(true)
    }
  }, [settingsData, loaded])

  const saveMutation = useMutation({
    mutationFn: () =>
      api.updateMobileSettings({ minAppVersion: minVersion.trim() }, token!),
    onSuccess: () => {
      toast.success("Impostazioni salvate")
      queryClient.invalidateQueries({ queryKey: ["mobile-settings"] })
    },
    onError: () => toast.error("Errore nel salvataggio"),
  })

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    saveMutation.mutate()
  }

  if (isLoading) {
    return <div className="py-8 text-center text-sm text-gray-500">Caricamento...</div>
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-base font-semibold text-gray-900">Configurazione App Mobile</h3>
        </div>
        <form onSubmit={handleSave} className="p-6">
          <div className="max-w-md space-y-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                Versione minima app mobile
              </label>
              <input
                type="text"
                value={minVersion}
                onChange={(e) => setMinVersion(e.target.value)}
                placeholder="es. 1.2.0"
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              <p className="text-xs text-gray-500">
                Gli utenti con una versione inferiore saranno forzati ad aggiornare l'app.
                Lascia vuoto per non forzare alcun aggiornamento.
              </p>
            </div>

            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saveMutation.isPending ? "Salvataggio..." : "Salva"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
